/**
 * This module registers commonly used UI components globally.
 * These come from /src/components/elements/* and /src/components/activix-ui/*.
 *
 * NOTE: there is some naming overlap between the two sets of components.
 * To use a specific component, you might want to import it directly, e.g.:
 *
 * // if you want to use the new component:
 * import ActivixButton from '@/components/activix-ui/ActivixButton/ActivixButton.vue';
 *
 * // if you want to use the old component:
 * import ActivixButton from '@/components/elements/ActivixButton.vue';
 *
 * NOTE: The icon component is registered under the name 'Icon' instead of 'ActivixIcon'.
 * This means that it overrides the default <icon></icon> HTML element.
 *
 * The rest of the components are registered with the Activix prefix,
 * e.g. <activix-button>
 */
import {
    ActivixPopover,
    ActivixPills,
    ActivixTabs,
    ActivixMultiselect,
} from '@/components/activix-ui/index.ts';
import ActivixAccordion from '../components/elements/ActivixAccordion.vue';
import ActivixAccordionItem from '../components/elements/ActivixAccordionItem.vue';
import ActivixAlert from '../components/elements/ActivixAlert.vue';
import ActivixAvatar from '../components/elements/ActivixAvatar.vue';
import ActivixBanner from '../components/elements/ActivixBanner.vue';
import ActivixBaseButton from '../components/elements/ActivixBaseButton.vue';
import ActivixButton from '../components/elements/ActivixButton.vue';
import ActivixCheckbox from '../components/elements/ActivixCheckbox.vue';
import ActivixConfirmModal from '../components/elements/ActivixConfirmModal.vue';
import ActivixIcon from '../components/elements/ActivixIcon.vue';
import ActivixInput from '../components/elements/ActivixInput.vue';
import ActivixLink from '../components/elements/ActivixLink.vue';
import ActivixMaskedInput from '../components/elements/ActivixMaskedInput.vue';
import ActivixRichRadio from '../components/elements/ActivixRichRadio.vue';
import ActivixRadio from '../components/elements/ActivixRadio.vue';
import ActivixReload from '../components/elements/ActivixReload.vue';
import ActivixRoundButton from '../components/elements/ActivixRoundButton.vue';
import ActivixSwitcher from '../components/elements/ActivixSwitcher.vue';
import ActivixSpinner from '../components/elements/ActivixSpinner.vue';
import ActivixTooltip from '../components/elements/ActivixTooltip.vue';
import ActivixCalendar from '../components/elements/ActivixCalendar.vue';
import ActivixTimePicker from '../components/elements/ActivixTimePicker.vue';
import ActivixUpload from '../components/elements/ActivixUpload.vue';
import ActivixHelperNotification from '../components/elements/ActivixHelperNotification.vue';

const activixUiPlugin = {
    install(app) {
        // Older Components - from /src/components/elements/*
        app.component(ActivixAccordion.name, ActivixAccordion);
        app.component(ActivixAccordionItem.name, ActivixAccordionItem);
        app.component(ActivixAlert.name, ActivixAlert);
        app.component(ActivixAvatar.name, ActivixAvatar);
        app.component(ActivixBanner.name, ActivixBanner);
        app.component(ActivixBaseButton.name, ActivixBaseButton);
        app.component(ActivixButton.name, ActivixButton);
        app.component(ActivixCheckbox.name, ActivixCheckbox);
        app.component(ActivixConfirmModal.name, ActivixConfirmModal);
        app.component(ActivixIcon.name, ActivixIcon);
        app.component(ActivixInput.name, ActivixInput);
        app.component(ActivixLink.name, ActivixLink);
        app.component(ActivixMaskedInput.name, ActivixMaskedInput);
        app.component(ActivixRichRadio.name, ActivixRichRadio);
        app.component(ActivixRadio.name, ActivixRadio);
        app.component(ActivixReload.name, ActivixReload);
        app.component(ActivixRoundButton.name, ActivixRoundButton);
        app.component(ActivixTooltip.name, ActivixTooltip);
        app.component(ActivixSpinner.name, ActivixSpinner);
        app.component(ActivixSwitcher.name, ActivixSwitcher);
        app.component(ActivixCalendar.name, ActivixCalendar);
        app.component(ActivixTimePicker.name, ActivixTimePicker);
        app.component(ActivixUpload.name, ActivixUpload);
        app.component(ActivixHelperNotification.name, ActivixHelperNotification);
        app.component('ActivixPills', ActivixPills);
        app.component('ActivixPopover', ActivixPopover);
        app.component('ActivixTabs', ActivixTabs);
        app.component('ActivixMultiselect', ActivixMultiselect);
    },
};

export default activixUiPlugin;
